import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OccurrenceChatUnreadResponse } from "@edu/shared";
import { nextAskMs } from "../../lib/poll.ts";
import { getOccurrenceChatUnread } from "./occurrence-chat-api.ts";

/**
 * Непрочитанное в чатах занятий видимой недели: точка в клетке расписания.
 *
 * ⚠️ **Спрашивается ДЕШЁВАЯ ручка, а не страница каждого чата** (раздел 7
 * проекта): в неделе два-три десятка занятий, и тридцать запросов ради точки
 * никто не делает. Ответ это три коротких поля на занятие, около 1,3 КБ сжатых
 * на полсотни строк, против 14 КБ у ОДНОЙ страницы сообщений.
 *
 * ⚠️ **Окно называется явно, а не угадывается сервером.** Человек листает
 * недели, и точки обязаны приезжать про ту, на которую он смотрит; прошедшие
 * сутки дописывает сервер сам, потому что писать можно ещё сутки после конца
 * занятия. Отсюда же зависимость эффекта от границ: сменил неделю, значит
 * спрашиваем заново.
 *
 * ⚠️ **Открытая карточка называется серверу подсказкой `?id=`.** Ответ режется
 * полусотней строк, и без подсказки счётчик слеп бы на пятьдесят первом занятии
 * МОЛЧА. Это не гипотеза: ровно так ослеп кабинетный счётчик разговоров, и это
 * чинили правкой (разбор у `ConversationUnreadResponse`). Подсказка едет через
 * `ref`, а не через зависимость эффекта: переключение карточек не должно
 * перезапускать таймер, иначе быстрое листание сетки означало бы запрос на
 * каждое нажатие.
 *
 * **Правила опроса.** Четыре из них живут в чистой `nextAskMs` (`lib/poll.ts`):
 * пауза на скрытой вкладке, отступ при обрыве, отсчёт от ответа. Флаг «без
 * нахлёста» и дедупликация по подписи стоят здесь: они про порядок вызовов и
 * про перерисовку, а не про сроки, и в чистую функцию их не выносили намеренно.
 *
 * ⚠️ **Перерисовка только по ИЗМЕНЕНИЮ подписи.** Ответ это новый объект, и
 * класть его в состояние двенадцать раз в минуту значило бы перерисовывать всю
 * сетку расписания поверх любой работы, включая набранное и не отправленное
 * сообщение в открытой карточке.
 *
 * ⚠️ **Отказ проглатывается молча.** Точка это справка, а не работа человека, и
 * красная строка «не удалось прочитать счётчик» поверх расписания была бы хуже
 * устаревшей точки. То же правило у счётчика переписки и у отметок «не понимаю».
 */

export interface ChatUnread {
  /**
   * Сколько непрочитанного у занятия по ЕГО адресу, ноль у незнакомого.
   *
   * ⚠️ **Ключ здесь это адрес ГЛАЗАМИ СМОТРЯЩЕГО, а не всегда ключ чата.** Общий
   * чат группы лежит под ключом группы, и преподаватель именно так свою клетку и
   * адресует; ученику сетка групповое занятие не сворачивает вовсе, поэтому ему
   * та же строка приезжает адресом его собственного слота. Сопоставлять строки с
   * клетками надо ПО ЭТОМУ адресу, а не по выведенному на клиенте.
   */
  unreadAt: (chatId: string) => number;
  /** Сумма по ВСЕМ занятиям окна, а не по приехавшим строкам. */
  total: number;
  /** Спросить немедленно: после отправки и после открытия карточки. */
  refresh: () => void;
  /** Какую карточку человек читает прямо сейчас. `null` означает «никакую». */
  watch: (chatId: string | null) => void;
}

const EMPTY_ITEMS: OccurrenceChatUnreadResponse["items"] = [];

function signatureOf(res: OccurrenceChatUnreadResponse): string {
  return `${String(res.total)}|${res.items
    .map((i) => `${i.id}:${String(i.unread)}:${String(i.at)}`)
    .join(",")}`;
}

/**
 * `enabled` это «сессия есть и неделя приехала»: спрашивать счётчик у того, кто
 * ещё не вошёл, нечем и незачем.
 *
 * ⚠️ Условие уезжает параметром, а не ветвлением вокруг вызова: состав хуков
 * обязан совпадать от рендера к рендеру, а сессия приезжает позже первого
 * рендера.
 */
export function useOccurrenceChatUnread(args: {
  from: string;
  to: string;
  enabled: boolean;
}): ChatUnread {
  const { from, to, enabled } = args;
  const [items, setItems] = useState<OccurrenceChatUnreadResponse["items"]>(EMPTY_ITEMS);
  const [total, setTotal] = useState(0);
  /** Живая ручка «спросить сейчас». Переставляется эффектом на каждый его запуск. */
  const askNow = useRef<() => void>(() => {});
  /** Открытая карточка. Через ref: перезапускать таймер из-за неё незачем. */
  const opened = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setItems(EMPTY_ITEMS);
      setTotal(0);
      return;
    }

    let alive = true;
    let timer: number | null = null;
    let running = false;
    let fails = 0;
    let seen = "";

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const plan = (ms: number | null) => {
      clearTimer();
      if (!alive || ms === null) return;
      timer = window.setTimeout(() => void ask(), ms);
    };

    const ask = async () => {
      timer = null;
      // Ни одного запроса внахлёст: медленный ответ, наложившись на свежий,
      // вернул бы точку на клетку, которую человек только что прочитал
      if (!alive || running) return;
      if (document.visibilityState === "hidden") return;

      running = true;
      try {
        const res = await getOccurrenceChatUnread({ from, to, pinned: opened.current });
        if (!alive) return;
        fails = 0;
        const next = signatureOf(res);
        if (next !== seen) {
          seen = next;
          setItems(res.items);
          setTotal(res.total);
        }
      } catch {
        // Молча: разбор в шапке файла
        fails += 1;
      } finally {
        running = false;
        if (alive) plan(nextAskMs({ visible: document.visibilityState === "visible", fails }));
      }
    };

    const wake = () => {
      if (document.visibilityState === "visible") {
        // Вернувшемуся человеку точки нужны сейчас, а не через пять секунд, и
        // отступ обнуляется вместе с этим: сеть могла починиться, пока он ушёл
        fails = 0;
        plan(0);
      } else {
        clearTimer();
      }
    };

    askNow.current = () => {
      fails = 0;
      plan(0);
    };
    document.addEventListener("visibilitychange", wake);
    void ask();

    return () => {
      alive = false;
      clearTimer();
      document.removeEventListener("visibilitychange", wake);
      askNow.current = () => {};
    };
  }, [enabled, from, to]);

  const refresh = useCallback(() => askNow.current(), []);
  const watch = useCallback((chatId: string | null) => {
    opened.current = chatId;
  }, []);

  return useMemo(() => {
    const by = new Map(items.map((i) => [i.id, i.unread]));
    return { unreadAt: (chatId: string) => by.get(chatId) ?? 0, total, refresh, watch };
  }, [items, total, refresh, watch]);
}
